import { useTranslation } from "react-i18next";
import {
  CheckOutlined,
  EditOutlined,
  LinkOutlined,
  LoadingOutlined,
  LogoutOutlined,
  PlusOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import type { Reservation, ReservationAction } from "../../../../api/reservations";
import { formatClock, minutesBetween } from "../../logic/dayTime";
import { arrivalStatus, type TableState } from "../../logic/tableStatus";
import { canUnseat, isSeatShown, seatHintOf, upcomingInSeatWindow, type UpcomingActions } from "../../logic/reservationActions";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import ReservationDetails from "./ReservationDetails";
import NextReservationNote from "./NextReservationNote";

const PRIMARY_BUTTON =
  "flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] border-none bg-brand text-[14px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_BUTTON =
  "flex h-9 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[9px] border border-shade-3 bg-surface px-3 text-[13px] font-semibold transition-colors duration-150 hover:bg-fill disabled:cursor-not-allowed disabled:opacity-60";

export interface PopoverHandlers {
  onSeat: (reservation: Reservation) => void;
  onRelease: (reservation: Reservation) => void;
  onCancel: (reservation: Reservation) => void;
  // „Редактирай" — дясната колона става „Редакция на резервация".
  onEdit: (reservation: Reservation) => void;
  // „Отмени настаняването" (с потвърждение).
  onUnseat: (reservation: Reservation) => void;
  // „Върни" на отказана (обратно в „Предстоящи").
  onRestore: (reservation: Reservation) => void;
  // Избраната отказана резервация, ако е на тази маса (клик на
  // картата ѝ в „Минали") — моментното състояние на масата не я съдържа.
  restorableOn: (tableId: number) => Reservation | null;
  // Кои действия има предстоящата (ден, маси, заетост — `upcomingActionsOf`).
  actionsOf: (reservation: Reservation) => UpcomingActions | null;
  // „Резервирай тази маса"; null — няма слот в/след момента (минал момент,
  // след 22:45, 24:00) и бутонът не се показва.
  onReserveTable: (() => void) | null;
  // Действието в ход (спинер + заключени бутони).
  pendingAction: ReservationAction | null;
}

const Spinner = ({ visible }: { visible: boolean }) => (visible ? <LoadingOutlined /> : null);

/**
 * Предстояща резервация: данните, реда за пристигането и действията
 * Настани / Откажи / Редактирай. „Настани" липсва за резервация
 * от друг ден; при маса на друга настанена резервация или по-рано от 30 мин
 * преди часа е изключен с обяснение (BE би върнал 409). При една отворена сметка по масите подсказката казва
 * към чия ще се закачи, при няколко — че ще питаме при кой сервитьор.
 */
const UpcomingReservation = ({
  reservation,
  nowMs,
  handlers,
}: {
  reservation: Reservation;
  nowMs: number;
  handlers: PopoverHandlers;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const busy = handlers.pendingAction !== null;
  const available = handlers.actionsOf(reservation);
  const seatBlock = available?.seatBlock ?? null;
  const hint = available ? seatHintOf(available) : null;
  const showSeat = isSeatShown(seatBlock);
  const arrival = arrivalStatus(reservation, nowMs);
  const statusLine = arrival ? (
    <span className={arrival.kind === "late" ? "text-danger-ink" : "text-brand-deep-ink"}>
      {t(arrival.kind === "late" ? "reservations.popover.late" : "reservations.popover.arriving", {
        duration: format.duration(arrival.minutes),
      })}
    </span>
  ) : null;
  return (
    <>
      <ReservationDetails reservation={reservation} statusLine={statusLine} />
      <div className="flex flex-col gap-2">
        {hint && seatBlock === "tooEarly" && (
          <div className="rounded-[8px] bg-fill px-2.5 py-2 text-[12.5px] font-semibold leading-[1.4] text-body">
            {t(hint.key, hint.params)}
          </div>
        )}
        {hint && seatBlock !== null && seatBlock !== "tooEarly" && (
          <div className="rounded-[8px] bg-danger-tint px-2.5 py-2 text-[12.5px] font-semibold leading-[1.4] text-danger-ink">
            {t(hint.key, hint.params)}
          </div>
        )}
        {hint && seatBlock === null && (
          <div className="flex items-start gap-2 rounded-[8px] bg-brand-tint px-2.5 py-2 text-[12.5px] font-semibold leading-[1.4] text-brand-deep-ink">
            <LinkOutlined className="mt-[3px] shrink-0" />
            {t(hint.key, hint.params)}
          </div>
        )}
        {showSeat && (
          <button
            type="button"
            className={PRIMARY_BUTTON}
            disabled={busy || seatBlock !== null}
            onClick={() => handlers.onSeat(reservation)}
          >
            {handlers.pendingAction === "seat" ? <LoadingOutlined /> : <CheckOutlined />}
            {t("reservations.popover.seat")}
          </button>
        )}
        <div className="flex flex-wrap gap-2 [&>button]:flex-[1_1_auto] [&>button]:px-2.5">
          <button
            type="button"
            className={`${SECONDARY_BUTTON} text-danger-ink`}
            disabled={busy}
            onClick={() => handlers.onCancel(reservation)}
          >
            <Spinner visible={handlers.pendingAction === "cancel"} />
            {t("reservations.popover.cancel")}
          </button>
          {available?.canEdit && (
            <button
              type="button"
              className={`${SECONDARY_BUTTON} text-ink`}
              disabled={busy}
              onClick={() => handlers.onEdit(reservation)}
            >
              <EditOutlined />
              {t("reservations.popover.edit")}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * „Резервация в този час": предстояща за масата, докато тя е заета от друго
 * (сметка без резервация) или свободна, но часът ѝ идва до 30 мин — със
 * същите действия (същият общ seat път), иначе няма откъде да се настани/откаже.
 */
const ReservedMeanwhile = ({
  reservation,
  nowMs,
  handlers,
}: {
  reservation: Reservation;
  nowMs: number;
  handlers: PopoverHandlers;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 border-t border-shade-2 pt-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.6px] text-muted">
        {t("reservations.popover.reservedMeanwhile")}
      </div>
      <UpcomingReservation reservation={reservation} nowMs={nowMs} handlers={handlers} />
    </div>
  );
};

/**
 * Отказана резервация на масата (избрана от „Минали"): данните
 * ѝ и „Върни" — масата в момента T не я показва (не държи масата).
 */
export const RestorableReservation = ({
  reservation,
  handlers,
}: {
  reservation: Reservation;
  handlers: PopoverHandlers;
}) => {
  const { t } = useTranslation();
  const statusLine = <span className="text-muted">{t("reservations.list.cancelled")}</span>;
  return (
    <div className="flex flex-col gap-3 border-t border-shade-2 pt-3">
      <ReservationDetails reservation={reservation} statusLine={statusLine} />
      <button
        type="button"
        className={`${SECONDARY_BUTTON} h-10 w-full text-[14px] font-bold text-brand-deep-ink`}
        disabled={handlers.pendingAction !== null}
        onClick={() => handlers.onRestore(reservation)}
      >
        {handlers.pendingAction === "restore" ? <LoadingOutlined /> : <UndoOutlined />}
        {t("reservations.popover.restore")}
      </button>
    </div>
  );
};

/**
 * Съдържанието на поповъра по състоянието на масата в момента T:
 * резервирана (Настани / Откажи / Редактирай), заета с настанена
 * резервация (Гостите си тръгнаха / Отмени настаняването), заета от приключила резервация или от сметка без
 * резервация (само данни + следващата резервация; ако в този час има и
 * предстояща резервация — тя с действията си, „Настани" изключен), свободна
 * (до кога + следващата + „Резервирай тази маса", ако има слот). На „сега"
 * (`isAtNow`) предстояща, чийто час идва до 30 мин, се показва като
 * „Резервация в този час" с „Настани гостите" и на свободна маса, и на
 * заета от сметка без резервация — както на картата.
 */
const TablePopoverBody = ({
  state,
  momentMs,
  nowMs,
  isAtNow,
  handlers,
}: {
  state: TableState;
  momentMs: number;
  nowMs: number;
  // Времевата линия е на „сега" (в „Преглед към" прозорецът за „Настани" не важи).
  isAtNow: boolean;
  handlers: PopoverHandlers;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const busy = handlers.pendingAction !== null;

  if (state.kind === "reserved") {
    return <UpcomingReservation reservation={state.reservation} nowMs={nowMs} handlers={handlers} />;
  }

  if (state.kind === "occupied") {
    const { reservation } = state;
    const minutes = minutesBetween(state.sinceMs, momentMs);
    const isSeated = reservation?.status === "seated";
    // Масата е заета от друго (сметка без резервация / още отворена сметка на
    // приключила), а в този час има предстояща резервация за нея — или такава
    // идва в прозореца на „Настани". Настанената държи масата — не се предлага.
    const meanwhileReservation =
      state.reservedMeanwhile ?? (isSeated ? null : upcomingInSeatWindow(state.next, nowMs, isAtNow));
    const meanwhile = meanwhileReservation && (
      <ReservedMeanwhile reservation={meanwhileReservation} nowMs={nowMs} handlers={handlers} />
    );
    if (!reservation) {
      return (
        <>
          <div className="text-[13px] font-semibold tabular-nums text-schema-occupied-ink">
            {t("reservations.popover.occupiedSince", {
              time: formatClock(state.sinceMs),
              duration: format.duration(minutes),
            })}
          </div>
          {meanwhile ?? (state.next && <NextReservationNote reservation={state.next} />)}
        </>
      );
    }
    const statusLine = (
      <span className="text-ink-2">
        {isSeated
          ? t("reservations.popover.seatedAt", {
              time: formatClock(state.sinceMs),
              duration: format.duration(minutes),
            })
          : t("reservations.popover.finished")}
      </span>
    );
    return (
      <>
        <ReservationDetails reservation={reservation} statusLine={statusLine} />
        {isSeated ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`${SECONDARY_BUTTON} h-10 flex-[1_1_auto] text-[14px] font-bold text-ink`}
              disabled={busy}
              onClick={() => handlers.onRelease(reservation)}
            >
              {handlers.pendingAction === "release" ? <LoadingOutlined /> : <LogoutOutlined />}
              {t("reservations.popover.release")}
            </button>
            {canUnseat(reservation, nowMs) && (
              <button
                type="button"
                className={`${SECONDARY_BUTTON} h-10 flex-[1_1_auto] text-body`}
                disabled={busy}
                onClick={() => handlers.onUnseat(reservation)}
              >
                {handlers.pendingAction === "unseat" ? <LoadingOutlined /> : <UndoOutlined />}
                {t("reservations.popover.unseat")}
              </button>
            )}
          </div>
        ) : (
          !meanwhile && state.next && <NextReservationNote reservation={state.next} />
        )}
        {meanwhile}
      </>
    );
  }

  // Свободна, но резервацията ѝ идва до 30 мин: „Настани гостите" вместо
  // бележката за следващата; „Резервирай тази маса" за този момент би се
  // застъпила с нея.
  const arrivingSoon = upcomingInSeatWindow(state.next, nowMs, isAtNow);
  return (
    <>
      <div className="text-[13.5px] font-bold tabular-nums text-schema-fits-ink">
        {state.freeUntilMs !== null
          ? t("reservations.popover.freeUntil", { time: formatClock(state.freeUntilMs) })
          : t("reservations.popover.freeAllDay")}
      </div>
      {arrivingSoon ? (
        <ReservedMeanwhile reservation={arrivingSoon} nowMs={nowMs} handlers={handlers} />
      ) : (
        <>
          {state.next && <NextReservationNote reservation={state.next} />}
          {handlers.onReserveTable && (
            <button type="button" className={PRIMARY_BUTTON} onClick={handlers.onReserveTable}>
              <PlusOutlined />
              {t("reservations.popover.reserveTable")}
            </button>
          )}
        </>
      )}
    </>
  );
};

export default TablePopoverBody;
